import { useProductBundleStore } from '@/features/Product/stores/productBundle';
import { useProductBaseStore } from '@/features/Product/stores/productBase';
import { useProductStockStore } from '@/features/Product/stores/productStock';
import { baseSkuCandidates } from '@/features/Product/composables/useBundleSavings';
import { vaultActive } from '@/features/Page/data/globalData';
import type { ProductComplete } from '@/features/Product/types/models';
import type { BundleCategory } from '@/features/Product/types/bundleTypes';
import type { LocationQuery, LocationQueryRaw, RouteLocationNormalized } from 'vue-router';

const VAULT_HANDLE = 'the-vault';
const VAULT_PATH = `/products/${VAULT_HANDLE}`;
const PRODUCT_ORDER_PARAM = 'po';
const REDIRECTABLE_ROUTES = ['home', 'collection', 'product', 'product-contextual'];
const CRAWLER_PATTERN =
  /bot|crawler|spider|crawling|facebookexternalhit|lighthouse|headlesschrome/i;

export type ProductOrderRanker = (product: ProductComplete) => number;

export type VaultRedirect = {
  path: string;
  query: LocationQueryRaw;
};

export const parseProductOrder = (query: LocationQuery): string[] => {
  const value = query[PRODUCT_ORDER_PARAM];

  return (Array.isArray(value) ? value : [value])
    .filter((entry): entry is string => typeof entry === 'string')
    .flatMap((entry) => entry.split(','))
    .map((entry) => entry.trim())
    .filter(Boolean);
};

export const createProductOrderRanker = (
  productOrder: string[],
  categories: Record<string, BundleCategory> = {},
): ProductOrderRanker => {
  const productBaseStore = useProductBaseStore();

  const targets = productOrder.map((value) => ({
    value,
    baseSku: productBaseStore.findProductByHandle(value)?.attributes.sku ?? null,
    categorySkus: categories[value]?.skus ?? [],
  }));

  return (product) => {
    const { sku, handle } = product.attributes;
    const candidates = baseSkuCandidates(sku);

    return targets.findIndex(
      ({ value, baseSku, categorySkus }) =>
        sku === value ||
        handle === value ||
        categorySkus.includes(sku) ||
        (!!baseSku && (sku === baseSku || candidates.includes(baseSku))),
    );
  };
};

const getVaultProduct = (): ProductComplete | null =>
  useProductBundleStore().completeBundleProducts.find(
    (product) => product.attributes.handle === VAULT_HANDLE,
  ) ?? null;

const getRouteHandle = (to: RouteLocationNormalized): string | null => {
  const value = to.params.parentHandle ?? to.params.handle;
  return typeof value === 'string' ? value : null;
};

export const resolveVaultRedirect = (to: RouteLocationNormalized): VaultRedirect | null => {
  if (!vaultActive) return null;
  if (window.global_vault_redirect !== true) return null;
  if (CRAWLER_PATTERN.test(navigator.userAgent)) return null;
  if (!REDIRECTABLE_ROUTES.includes(String(to.name))) return null;

  const routeHandle = getRouteHandle(to);
  if (routeHandle === VAULT_HANDLE) return null;

  const vault = getVaultProduct();
  if (!vault || useProductStockStore().isProductSoldOut(vault)) return null;

  const excludedHandles = vault.bundleDefinition?.additionalBundleDetails?.redirect?.excludeHandles;
  if (routeHandle && excludedHandles?.includes(routeHandle)) return null;

  const requestedOrder = parseProductOrder(to.query);
  const productOrder = requestedOrder.length ? requestedOrder : routeHandle ? [routeHandle] : [];

  return {
    path: VAULT_PATH,
    query: {
      ...to.query,
      ...(productOrder.length && { [PRODUCT_ORDER_PARAM]: productOrder.join(',') }),
    },
  };
};
